import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { billingService } from '@/services/billingService'
import type { BillingAccountInput, BillingPack, BillingReportGroup } from '@/types/billing'
import { queryKeys } from '../keys'

export function useBillingAccountsQuery() {
  return useQuery({ queryKey: queryKeys.billing.accounts(), queryFn: () => billingService.listAccounts() })
}

export function useBillingSalesQuery(accountId: number | null) {
  return useQuery({
    queryKey: queryKeys.billing.sales(accountId ?? 0),
    queryFn: () => billingService.listSales(accountId as number),
    enabled: accountId !== null,
  })
}

export function useBillingBalancesQuery(accountId: number | null) {
  return useQuery({
    queryKey: queryKeys.billing.balances(accountId ?? 0),
    queryFn: () => billingService.listBalances(accountId as number),
    enabled: accountId !== null,
  })
}

export function useBillingLapsedQuery(accountId: number | null) {
  return useQuery({
    queryKey: queryKeys.billing.lapsed(accountId ?? 0),
    queryFn: () => billingService.listLapsed(accountId as number),
    enabled: accountId !== null,
  })
}

export function useBillingMarginQuery(accountId: number | null, start: string, end: string, group: BillingReportGroup) {
  return useQuery({
    queryKey: queryKeys.billing.margin(accountId ?? 0, start, end, group),
    queryFn: () => billingService.margin(accountId as number, start, end, group),
    enabled: accountId !== null,
  })
}

function useInvalidateAccounts() {
  const client = useQueryClient()
  return () => client.invalidateQueries({ queryKey: queryKeys.billing.accounts() })
}

export function useCreateBillingAccount() {
  const invalidate = useInvalidateAccounts()
  return useMutation({ mutationFn: (input: BillingAccountInput) => billingService.createAccount(input), onSuccess: invalidate })
}

export function useUpdateBillingAccount() {
  const invalidate = useInvalidateAccounts()
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: Partial<BillingAccountInput> }) => billingService.updateAccount(id, input),
    onSuccess: invalidate,
  })
}

export function useIssueBillingKey() {
  const invalidate = useInvalidateAccounts()
  return useMutation({
    mutationFn: ({ id, label }: { id: number; label: string }) => billingService.issueKey(id, label),
    onSuccess: invalidate,
  })
}

export function useRevokeBillingKey() {
  const invalidate = useInvalidateAccounts()
  return useMutation({
    mutationFn: ({ id, prefix }: { id: number; prefix: string }) => billingService.revokeKey(id, prefix),
    onSuccess: invalidate,
  })
}

export function useFinishStripeLink() {
  const invalidate = useInvalidateAccounts()
  return useMutation({
    mutationFn: ({ code, state }: { code: string; state: string }) => billingService.finishLinkExisting(code, state),
    onSuccess: invalidate,
  })
}

export function useSaveBillingPack() {
  const invalidate = useInvalidateAccounts()
  return useMutation({
    mutationFn: ({ id, pack }: { id: number; pack: BillingPack }) => billingService.savePack(id, pack),
    onSuccess: invalidate,
  })
}
